import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Sparkles } from "lucide-react";
import "@/App.css";
import "@/utils/api"; // global axios 401 interceptor

// Error Boundary
import ErrorBoundary, { setupGlobalErrorHandling } from "@/components/ErrorBoundary";

// Setup global error handling on app load
setupGlobalErrorHandling();

// Pages
import Landing from "@/pages/Landing";
import LandingNew from "@/pages/LandingNew";
import SeoCreatorLanding from "@/pages/SeoCreatorLanding";
import ScoutStudio from "@/pages/ScoutStudio";
import ScoutOpiekun from "@/pages/ScoutOpiekun";
import BirchHausPreview from "@/pages/BirchHausPreview";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ResetPassword from "@/pages/ResetPassword";
import SetNewPassword from "@/pages/SetNewPassword";
import DeleteAccount from "@/pages/DeleteAccount";
import Dashboard from "@/pages/Dashboard";
import DashboardLight from "@/pages/DashboardLight";
import PostTrialScreen from "@/pages/PostTrialScreen";
import Account from "@/pages/Account";
import SettingsHome from "@/pages/SettingsHome";
import KennelWizard from "@/pages/KennelWizard";
import KennelWizardV2 from "@/pages/KennelWizardV2";
import SukiV2 from "@/pages/modules/SukiV2";
import ReproduktoryV2 from "@/pages/modules/ReproduktoryV2";
import MiotyV2 from "@/pages/modules/MiotyV2";
import StronaHodowliRedirect from "@/pages/StronaHodowliRedirect";
import StronaFirmyRedirect from "@/pages/StronaFirmyRedirect";
import MyProfiles from "@/pages/MyProfiles";
import ProfileTypePicker from "@/pages/ProfileTypePicker";
import PostCreatorV2 from "@/pages/PostCreatorV2";
import LitterWizard from "@/pages/LitterWizard";
import LittersManager from "@/pages/LittersManager";
import StudWizard from "@/pages/StudWizard";
import DogEdit from "@/pages/DogEdit";
import DogWizard from "@/pages/DogWizardNew";
import DogProfile from "@/pages/DogProfile";
import Leads from "@/pages/LeadsManager";
import MyPages from "@/pages/MyPages";
import Recommendations from "@/pages/Recommendations";
import AIScan from "@/pages/AIScan";
import DogsManager from "@/pages/DogsManager";
import AdsCreator from "@/pages/AdsCreator";
import Statistics from "@/pages/Statistics";
import KennelRouter from "@/pages/KennelRouter";
import PanelLayout from "@/components/PanelLayout";
import PublicLitter from "@/pages/PublicLitter";
import PublicDog from "@/pages/PublicDog";
import PublicStud from "@/pages/PublicStud";
import BreedPage from "@/pages/BreedPage";
import KatalogPage from "@/pages/KatalogPage";
import BreedsListPage from "@/pages/BreedsListPage";
import RedirectToKatalog from "@/components/RedirectToKatalog";
import FciGroupsIndex from "@/pages/FciGroupsIndex";
import FciGroupPage from "@/pages/FciGroupPage";
import AddBreederPage from "@/pages/AddBreederPage";
import EditBreederPage from "@/pages/EditBreederPage";
import AddBusinessPage from "@/pages/AddBusinessPage";
import EditBusinessPage from "@/pages/EditBusinessPage";
import CityBreedPage from "@/pages/CityBreedPage";
import LittersByLocation from "@/pages/LittersByLocation";
import KennelsByLocation from "@/pages/KennelsByLocation";
import KennelsByCity from "@/pages/KennelsByCity";
import StudsByCity from "@/pages/StudsByCity";
import StudsListingPage from "@/pages/StudsListingPage";
import StudsBreedPage from "@/pages/StudsBreedPage";
import StudsCityPage from "@/pages/StudsCityPage";
import Oferta from "@/pages/Oferta";
import Contact from "@/pages/Contact";
import Regulamin from "@/pages/Regulamin";
import PolitykaPrywatnosci from "@/pages/PolitykaPrywatnosci";
import PolitykaCookies from "@/pages/PolitykaCookies";
import AuthCallback from "@/pages/AuthCallback";
import MyProjects from "@/pages/MyProjects";
import EmailSettings from "@/pages/EmailSettings";
import WebhooksSettings from "@/pages/WebhooksSettings";
import AdminDogs from "@/pages/AdminDogs";
import AdminKennels from "@/pages/AdminKennels";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminFunnel from "@/pages/AdminFunnel";
import AdminModeration from "@/pages/AdminModeration";
import AdminUsers from "@/pages/AdminUsers";
import DemoStart from "@/pages/DemoStart";
import ConnectWebsite from "@/pages/ConnectWebsite";
import AdminStats from "@/pages/AdminStats";
import AdminPanel from "@/pages/AdminPanel";
import AdminAutoPublishDebug from "@/pages/AdminAutoPublishDebug";
import AgencyPanel from "@/pages/AgencyPanel";
import ExternalSitePage from "@/pages/ExternalSitePage";
// ShareCenter removed - now integrated as ShareActions in ConnectWebsite.js (/panel/moja-strona)
// New V2 Public Pages (component-based)
import PublicKennelV2 from "@/pages/PublicKennelV2";
import PublicStudV2 from "@/pages/PublicStudV2";
import PublicLitterV2 from "@/pages/PublicLitterV2";
// Academy
import AcademyPage from "@/pages/AcademyPage";
// Blog
import Blog from "@/pages/Blog";
import BlogArticle from "@/pages/BlogArticle";

// Dedicated Landing Pages
import LandingHodowcy from "@/pages/LandingHodowcy";
import MojeWyszukiwania from "@/pages/MojeWyszukiwania";
import MojeObserwowane from "@/pages/MojeObserwowane";
import LandingFirmy from "@/pages/LandingFirmy";
import WolfeCaseStudyPage from "@/pages/WolfeCaseStudyPage";
import DemoHodowla from "@/pages/DemoHodowla";
import DemoPanel from "@/pages/DemoPanel";

// Billing Pages
import PricingPage from "@/pages/PricingPage";
import BillingSuccess from "@/pages/BillingSuccess";

// Booster V2 New - 4-step wizard with 2-column layout
import { BoosterV2NewHome, BoosterV2Wizard as BoosterV2NewWizard, BoosterV2Plan as BoosterV2NewPlan } from "@/pages/BoosterV2New";

// Pomoc - AI Assistant
import PomocPage from "@/pages/PomocPage";
import OpiekunkaPage from "@/pages/OpiekunkaPage";
import PremiumServiceLead from "@/pages/PremiumServiceLead";
import AssistantPage from "@/pages/AssistantPage";

// Wizytówki Google - P0
import { WizytowkiLanding, WizytowkiWizard, WizytowkiAdmin } from "@/pages/Wizytowki";

// Business - mikrofirmy
import BusinessWizard from "@/pages/BusinessWizard";
import BusinessWizardV2 from "@/pages/BusinessWizardV2";
import BusinessPanel from "@/pages/BusinessPanel";
import PanelOferta from "@/pages/PanelOferta";
import PanelOpinie from "@/pages/PanelOpinie";
import ReviewSubmit from "@/pages/ReviewSubmit";

// Katalog biznesowy - programmatic SEO
import BusinessCatalogIndex from "@/pages/BusinessCatalogIndex";
import BusinessCatalogPage from "@/pages/BusinessCatalogPage";
import BusinessCatalogCity from "@/pages/BusinessCatalogCity";
import KennelsCatalog from "@/pages/KennelsCatalog";
import MiotyListingPage from "@/pages/MiotyListingPage";

// Łowicka 25 Landing Page
import LowickaLanding from "@/pages/LowickaLanding";
import PublicBusiness from "@/pages/PublicBusiness";
import StworzCreator from "@/pages/StworzCreator";
import StworzFinish from "@/pages/StworzFinish";
import AutoPublish from "@/pages/AutoPublish";
import DmTemplatesSettings from "@/pages/DmTemplatesSettings";
import Publikacja from "@/pages/Publikacja";
import BoosterHome from "@/pages/BoosterHome";
import DashboardHome from "@/pages/DashboardHome";
import DashboardHomeV2 from "@/pages/DashboardHomeV2";
import PanelKatalog from "@/pages/PanelKatalog";
import PanelPromocja from "@/pages/PanelPromocja";
import CampaignDashboard from "@/pages/CampaignDashboard";
import GdziePublikowac from "@/pages/GdziePublikowac";
import CampaignLibrary from "@/pages/CampaignLibrary";
import CampaignView from "@/pages/CampaignView";
import CampaignLanding from "@/pages/CampaignLanding";
import DemoCampaign from "@/pages/DemoCampaign";
import CompetitorWatch from "@/pages/CompetitorWatch";
import LeadNurturing from "@/pages/LeadNurturing";
import AutoBoostGuide from "@/pages/AutoBoostGuide";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";
import CalendarPage from "@/pages/CalendarPage";
import KalendarzCiazy from "@/pages/KalendarzCiazy";
import ResultsPage from "@/pages/ResultsPage";
import PuppyAdGenerator from "@/pages/PuppyAdGenerator";
import PosterGenerator from "@/pages/PosterGenerator";
import ProfilePanel from "@/pages/ProfilePanel";
import PageBuilder from "@/pages/PageBuilder";
import HeroCreator from "@/pages/HeroCreator";
import SubskrypcjaPage from "@/pages/SubskrypcjaPage";
import MarketingHub from "@/pages/MarketingHub";
import GalleryPage from "@/pages/GalleryPage";
import GroupPostingFlow from "@/pages/GroupPostingFlow";
import PublicPage from "@/pages/PublicPage";

// Onboarding - AI Interview Flow
import Onboarding from "@/pages/Onboarding";

// Components
import CookieConsent from "@/components/CookieConsent";

// Context
import { AuthProvider } from "@/context/AuthContext";

import { Helmet } from 'react-helmet-async';

// NoIndex dla stron panelowych (admin, panel, logowanie)
function NoIndexForPrivatePages() {
  const location = useLocation();
  const path = location.pathname;
  const isPrivate = path.startsWith('/panel') || path.startsWith('/admin') || 
    path === '/logowanie' || path === '/login' || path === '/rejestracja' || 
    path === '/register' || path === '/reset-hasla' || path.startsWith('/wizard') || path.startsWith('/start');
  
  if (!isPrivate) return null;
  return (
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );
}

// AppRouter - handles session_id detection synchronously during render
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google OAuth callback)
  // This MUST be synchronous during render to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
    <NoIndexForPrivatePages />
    <Routes>
      {/* ============================================================ */}
      {/* PUBLIC MARKETPLACE — segmenty: 🐕 HODOWCY + 🏢 BIZNES         */}
      {/* Wspólny: TopNavBar, MarketplaceFooter, system leadów, layout */}
      {/* ============================================================ */}

      {/* === 📌 LANDING + AUTH === */}
      <Route path="/" element={<LandingNew />} />
      <Route path="/birch-haus" element={<BirchHausPreview />} />
      <Route path="/us/birch-haus" element={<BirchHausPreview />} />
      <Route path="/historie-sukcesu/wolfe-vom-katzenberg" element={<WolfeCaseStudyPage />} />
      <Route path="/cennik" element={<Navigate to="/panel/cennik" replace />} />
      <Route path="/produkt" element={<Navigate to="/" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/logowanie" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/register" element={<Register />} />
      <Route path="/rejestracja" element={<Register />} />
      <Route path="/reset-hasla" element={<ResetPassword />} />
      <Route path="/reset-hasla/:token" element={<SetNewPassword />} />
      <Route path="/usun-konto/:token" element={<DeleteAccount />} />
      <Route path="/kontakt" element={<Contact />} />

      {/* === 🐕 SEGMENT 1: HODOWCY (psy/mioty/reproduktory) === */}
      <Route path="/dla-hodowcy" element={<LandingHodowcy />} />
      <Route path="/katalog" element={<KatalogPage />} />
      <Route path="/scout" element={<ScoutStudio />} />
      <Route path="/scout/opiekun" element={<ScoutOpiekun />} />
      <Route path="/szukaj" element={<KatalogPage />} />
      <Route path="/szczenieta" element={<Navigate to="/katalog?type=mioty" replace />} />
      <Route path="/hodowle" element={<Navigate to="/katalog?type=hodowle" replace />} />
      <Route path="/mioty" element={<Navigate to="/katalog?type=mioty" replace />} />
      <Route path="/reproduktory" element={<Navigate to="/katalog?type=reproduktory" replace />} />
      <Route path="/rasy" element={<Navigate to="/katalog?type=mioty" replace />} />
      <Route path="/rasy/:breed" element={<BreedPage />} />
      <Route path="/rasy/:breed/:section" element={<BreedPage />} />
      <Route path="/grupy-fci" element={<FciGroupsIndex />} />
      <Route path="/grupy-fci/:groupSlug" element={<FciGroupPage />} />

      {/* Hodowcy — szczegóły encji (publiczne) */}
      <Route path="/hodowla/:slug" element={<KennelRouter />} />
      <Route path="/miot/:slug" element={<PublicLitter />} />
      <Route path="/pies/:slug" element={<PublicDog />} />
      <Route path="/reproduktor/:slug" element={<PublicStud />} />

      {/* Hodowcy — formularze publiczne */}
      <Route path="/dodaj-hodowle" element={<AddBreederPage />} />
      <Route path="/stworz" element={<StworzCreator />} />
      <Route path="/stworz/dokoncz" element={<StworzFinish />} />
      <Route path="/stworz-strone" element={<StworzCreator />} />
      <Route path="/dodaj-ogloszenie" element={<Navigate to="/dodaj-hodowle" replace />} />
      <Route path="/edytuj-hodowle/:token" element={<EditBreederPage />} />

      {/* Hodowcy — Programmatic SEO redirecty (zachowane jako entry points dla Google) */}
      <Route path="/szczeniaki/:breedSlug/:citySlug" element={<RedirectToKatalog type="mioty" breedParam="breedSlug" cityParam="citySlug" />} />
      <Route path="/mioty/:breed/:voivodeship" element={<RedirectToKatalog type="mioty" breedParam="breed" voivodeshipParam="voivodeship" />} />
      <Route path="/hodowle/:breed/:voivodeship" element={<RedirectToKatalog type="hodowle" breedParam="breed" voivodeshipParam="voivodeship" />} />
      <Route path="/hodowle-miasto/:breed/:city" element={<RedirectToKatalog type="hodowle" breedParam="breed" cityParam="city" />} />
      <Route path="/reproduktory-miasto/:breed/:city" element={<RedirectToKatalog type="reproduktory" breedParam="breed" cityParam="city" />} />
      <Route path="/reproduktory/:breedSlug/:citySlug" element={<RedirectToKatalog type="reproduktory" breedParam="breedSlug" cityParam="citySlug" />} />
      <Route path="/reproduktory/:breedSlug" element={<RedirectToKatalog type="reproduktory" breedParam="breedSlug" />} />

      {/* === 🏢 SEGMENT 2: BIZNES (firmy/usługi/wizytówki) === */}
      <Route path="/dla-firm" element={<LandingFirmy />} />
      <Route path="/dla-firm-uslugowych" element={<LandingFirmy />} />
      <Route path="/strona-dla-hodowli" element={<SeoCreatorLanding variant="hodowla" />} />
      <Route path="/strona-dla-hodowli/:slug" element={<SeoCreatorLanding variant="hodowla" />} />
      <Route path="/strona-dla-firmy" element={<SeoCreatorLanding variant="firma" />} />
      <Route path="/strona-dla-firmy/:slug" element={<SeoCreatorLanding variant="firma" />} />
      <Route path="/firmy" element={<Navigate to="/katalog/uslugi" replace />} />
      <Route path="/uslugi" element={<Navigate to="/katalog/uslugi" replace />} />
      <Route path="/katalog/uslugi" element={<BusinessCatalogIndex />} />
      <Route path="/katalog/miasto/:city" element={<BusinessCatalogCity />} />
      <Route path="/katalog/:category" element={<BusinessCatalogPage />} />
      <Route path="/katalog/:category/:city" element={<BusinessCatalogPage />} />

      {/* Biznes — szczegóły encji + formularze */}
      <Route path="/firma/:slug" element={<PublicBusiness />} />
      <Route path="/dodaj-firme" element={<AddBusinessPage />} />
      <Route path="/edytuj-firme/:token" element={<EditBusinessPage />} />
      <Route path="/wizytowki" element={<WizytowkiLanding />} />
      <Route path="/wizytowki/start" element={<WizytowkiWizard />} />

      {/* === 📜 LEGAL === */}
      <Route path="/regulamin" element={<Regulamin />} />
      <Route path="/polityka-prywatnosci" element={<PolitykaPrywatnosci />} />
      <Route path="/polityka-cookies" element={<PolitykaCookies />} />

      {/* === 📰 BLOG SEO === */}
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />

      {/* === 👤 PUBLIC USER PAGE === */}
      <Route path="/s/:slug" element={<PublicPage />} />
      <Route path="/moje/wyszukiwania" element={<MojeWyszukiwania />} />
      <Route path="/moje/obserwowane" element={<MojeObserwowane />} />

      {/* ============================================================ */}
      {/* 🚫 LEGACY / TESTOWE / MOCKUPY — odpięte z routingu publicznego */}
      {/* Pliki zostają w kodzie, ale URL przekierowuje do `/`           */}
      {/* (nie usuwam fizycznie — można przywrócić jeśli będą potrzebne) */}
      {/* ============================================================ */}
      <Route path="/strona-startowa" element={<Navigate to="/" replace />} />
      <Route path="/stary" element={<Navigate to="/" replace />} />
      <Route path="/dla-hodowcow" element={<Navigate to="/dla-hodowcy" replace />} />
      <Route path="/oferta" element={<Navigate to="/dla-hodowcy" replace />} />
      <Route path="/demo" element={<DemoStart />} />
      <Route path="/demo-panel" element={<Navigate to="/" replace />} />
      <Route path="/demo-kampania" element={<Navigate to="/" replace />} />
      <Route path="/lowicka" element={<Navigate to="/" replace />} />
      <Route path="/v2/hodowla/:slug" element={<Navigate to="/katalog?type=hodowle" replace />} />
      <Route path="/v2/miot/:slug" element={<Navigate to="/katalog?type=mioty" replace />} />
      <Route path="/v2/reproduktor/:slug" element={<Navigate to="/katalog?type=reproduktory" replace />} />
      <Route path="/kampania/:packId" element={<Navigate to="/" replace />} />
      
      {/* Wizards - STANDALONE (no PanelLayout, uses WizardShell) */}
      {/* /start/wybor — wycofane. Wybór typu strony przeniesiony do Creative Studio.
          Zachowujemy redirect dla ewentualnych starych linków. */}
      <Route path="/start/wybor" element={<Navigate to="/panel/kreator-top" replace />} />
      <Route path="/panel/moje" element={<MyProfiles />} />
      {/* Legacy Kreator Hodowli (7 kroków, własny shell) wygaszony — przekierowanie do V2 */}
      <Route path="/wizard/hodowla" element={<StronaHodowliRedirect />} />
      <Route path="/wizard/hodowla/:id" element={<StronaHodowliRedirect />} />
      <Route path="/wizard/hodowla-v2" element={<KennelWizardV2 />} />
      <Route path="/wizard/hodowla-v2/:id" element={<KennelWizardV2 />} />
      <Route path="/onboarding/profile-type" element={<ProfileTypePicker />} />
      <Route path="/panel/kreator-postu" element={<PostCreatorV2 />} />
      <Route path="/wizard/miot" element={<LitterWizard />} />
      <Route path="/wizard/miot/:id" element={<LitterWizard />} />
      <Route path="/wizard/pies/:kennelId" element={<DogWizard />} />
      <Route path="/wizard/pies/:kennelId/:dogId" element={<DogWizard />} />
      <Route path="/wizard/reproduktor" element={<StudWizard />} />
      <Route path="/wizard/reproduktor/:id" element={<StudWizard />} />
      <Route path="/panel/reproduktor/:id/edytuj" element={<StudWizard />} />
      
      {/* Business Wizard */}
      <Route path="/wizard/firma" element={<BusinessWizard />} />
      <Route path="/wizard/firma/:id" element={<BusinessWizard />} />
      <Route path="/wizard/firma-v2" element={<BusinessWizardV2 />} />
      <Route path="/wizard/firma-v2/:id" element={<BusinessWizardV2 />} />
      
      {/* Dashboard - Panel pages (use PanelLayout) */}
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/panel" element={<DashboardHomeV2 />} />
      <Route path="/panel/katalog" element={<PanelKatalog />} />
      <Route path="/panel/promocja" element={<PanelPromocja />} />
      <Route path="/panel-legacy" element={<DashboardHome />} />
      <Route path="/panel/kampania" element={<CampaignDashboard />} />
      <Route path="/panel/gdzie-publikowac" element={<GdziePublikowac />} />
      <Route path="/panel/competitor-watch" element={<CompetitorWatch />} />
      <Route path="/panel/lead-nurturing" element={<LeadNurturing />} />
      <Route path="/panel/auto-boost" element={<AutoBoostGuide />} />
      <Route path="/panel-old" element={<DashboardLight />} />
      <Route path="/panel/marketing" element={<Navigate to="/panel/publikacja" replace />} />
      <Route path="/panel/ustawienia" element={<SettingsHome />} />
      <Route path="/panel/ustawienia/konto" element={<Account />} />
      <Route path="/panel/ustawienia/platnosci" element={<PricingPage />} />
      <Route path="/panel/konto" element={<Account />} />
      <Route path="/panel/ustawienia/szablony-dm" element={<DmTemplatesSettings />} />
      <Route path="/panel/pomoc" element={<PomocPage />} />
      <Route path="/panel/opiekunka" element={<OpiekunkaPage />} />
      <Route path="/pomoc" element={<OpiekunkaPage />} />
      <Route path="/panel/uslugi-premium" element={<PremiumServiceLead />} />
      <Route path="/panel/asystent" element={<AssistantPage />} />
      <Route path="/panel/ai-scan" element={<AIScan />} />
      <Route path="/panel/reproduktor" element={<Navigate to="/panel/reproduktory" replace />} />
      <Route path="/panel/moja-strona" element={<ConnectWebsite />} />
      <Route path="/panel/mam-strone" element={<ExternalSitePage />} />
      <Route path="/panel/hodowla/:kennelId/psy" element={<Navigate to="/panel/profil?tab=dogs" replace />} />
      <Route path="/panel/pies/:id" element={<DogProfile />} />
      <Route path="/panel/pies/:id/edytuj" element={<DogWizard />} />
      <Route path="/panel/mioty" element={<MiotyV2 />} />
      <Route path="/panel/psy" element={<SukiV2 />} />
      <Route path="/panel/suki" element={<SukiV2 />} />
      <Route path="/panel/reproduktory" element={<ReproduktoryV2 />} />
      <Route path="/panel/zapytania" element={<Leads />} />
      <Route path="/panel/leady/:leadId" element={<Leads />} />
      <Route path="/panel/leady" element={<Leads />} />
      <Route path="/panel/strony" element={<Navigate to="/panel/moje" replace />} />
      <Route path="/panel/widocznosc" element={<Navigate to="/panel/moje" replace />} />
      <Route path="/panel/rekomendacje" element={<Recommendations />} />
      <Route path="/panel/statystyki" element={<Statistics />} />
      <Route path="/panel/akademia" element={<AcademyPage />} />
      <Route path="/panel/projekty" element={<MyProjects />} />
      <Route path="/panel/uslugi" element={<BusinessPanel />} />
      <Route path="/panel/oferta" element={<PanelOferta />} />
      <Route path="/panel/opinie" element={<PanelOpinie />} />
      <Route path="/opinia/:token" element={<ReviewSubmit />} />
      <Route path="/panel/galeria" element={<GalleryPage />} />
      <Route path="/panel/auto-publikacja" element={<AutoPublish />} />
      <Route path="/panel/grupy" element={<GroupPostingFlow />} />
      <Route path="/panel/publikacja" element={<Publikacja />} />
      <Route path="/panel/kreator-reklam" element={
        <PanelLayout>
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="w-20 h-20 rounded-2xl bg-orange-50 flex items-center justify-center mb-6">
              <Sparkles className="w-10 h-10 text-orange-400" />
            </div>
            <h2 className="text-2xl font-bold text-zinc-900 mb-2">Kreator grafik</h2>
            <p className="text-base text-zinc-500 mb-1">Dopieszczamy ten modul dla Ciebie</p>
            <span className="inline-block text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-orange-100 text-orange-500 border border-orange-200 mt-3">
              Nowosc wkrotce
            </span>
          </div>
        </PanelLayout>
      } />
      <Route path="/panel/kalendarz" element={<CalendarPage />} />
      <Route path="/panel/kalendarz-ciazy" element={<KalendarzCiazy />} />
      <Route path="/panel/wyniki" element={<ResultsPage />} />
      
      {/* Booster - Campaign tools (accessible from Marketing Hub) */}
      <Route path="/panel/booster" element={<BoosterHome />} />
      <Route path="/panel/booster/kampanie" element={<BoosterHome />} />
      <Route path="/panel/booster/wyniki" element={<BoosterHome />} />
      <Route path="/panel/booster/tresci" element={<BoosterHome />} />
      <Route path="/panel/booster/visibility" element={<BoosterHome />} />
      <Route path="/panel/biblioteka" element={<CampaignLibrary />} />
      <Route path="/panel/analityka" element={<AnalyticsDashboard />} />
      <Route path="/panel/ogloszenie-szczeniat" element={<PuppyAdGenerator />} />
      <Route path="/panel/grafiki" element={<PosterGenerator />} />
      <Route path="/panel/strona-hodowli" element={<StronaHodowliRedirect />} />
      <Route path="/panel/strona-firmy" element={<StronaFirmyRedirect />} />      <Route path="/panel/profil" element={<ProfilePanel />} />
      <Route path="/panel/strona" element={<PageBuilder />} />
      <Route path="/panel/kreator-top" element={<HeroCreator />} />
      <Route path="/panel/subskrypcja" element={<SubskrypcjaPage />} />
      <Route path="/panel/booster/kampania/:packId" element={<CampaignView />} />
      
      {/* Booster V2 - 4-step wizard */}
      <Route path="/panel/booster-v2-new" element={<BoosterV2NewHome />} />
      <Route path="/panel/booster-v2-new/wizard/:planId" element={<BoosterV2NewWizard />} />
      <Route path="/panel/booster-v2-new/plan/:planId" element={<BoosterV2NewPlan />} />
      
      <Route path="/panel/ustawienia/email" element={<EmailSettings />} />
      <Route path="/panel/ustawienia/webhooks" element={<WebhooksSettings />} />
      
      {/* Billing routes */}
      <Route path="/panel/cennik" element={<PricingPage />} />
      <Route path="/panel/trial-zakonczony" element={<PostTrialScreen />} />
      <Route path="/panel/billing/success" element={<BillingSuccess />} />
      <Route path="/panel/billing/cancel" element={<PricingPage />} />
      
      {/* Admin - Stats */}
      <Route path="/admin/stats" element={<AdminStats />} />
      
      {/* Admin - Moderation System */}
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/admin/funnel" element={<AdminFunnel />} />
      <Route path="/admin/moderation" element={<AdminModeration />} />
      <Route path="/admin/users" element={<AdminUsers />} />
      
      {/* Admin - Dogs Management */}
      <Route path="/admin/dogs" element={<AdminDogs />} />
      <Route path="/admin/kennels" element={<AdminKennels />} />
      
      {/* Admin - Wizytówki Google */}
      <Route path="/admin/wizytowki" element={<WizytowkiAdmin />} />
      
      {/* Admin - Business Control Panel */}
      <Route path="/admin" element={<Navigate to="/admin/control" replace />} />
      <Route path="/admin/control" element={<AdminPanel />} />
      <Route path="/admin/auto-publish" element={<AdminAutoPublishDebug />} />
      <Route path="/admin/booster" element={<AgencyPanel />} />
    </Routes>
    </>
  );
}

function CookieConsentGate() {
  const { pathname } = useLocation();
  // Ukryj globalny (polski) baner cookies na izolowanej anglojęzycznej makiecie US.
  if (pathname === "/birch-haus" || pathname === "/us/birch-haus") return null;
  return <CookieConsent />;
}

function App() {
  return (
    <ErrorBoundary componentName="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <CookieConsentGate />
          <Toaster 
            position="top-center" 
            richColors 
            toastOptions={{
              style: { background: '#FFFFFF', border: '1px solid #E8EAED', color: '#1A1D26', fontFamily: "'DM Sans', sans-serif" }
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
